import { Box, Group, Loader, SegmentedControl, Text, UnstyledButton } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { IconUpload } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { CSSProperties, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { HubTable } from '@/components/Hub/HubTable';
import { LoadFailed } from '@/components/Hub/LoadFailed';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { fetchBotRead, fetchBotWrite } from '@/services/botCalls';
import { showUndoToast } from '@/services/undoToast';
import { ORBIT_DISABLED, ORBIT_GRAY, ORBIT_NEEDS_ACTION } from '@/theme/colors';
import { LedgerCell } from '../ledger/LedgerCell';
import { formatLedgerDate, LEDGER_NO_PATIENT_PARAM, sourceLabel } from '../ledger/ledgerRows';
import { ImportPreviewTable } from './ImportPreviewTable';
import {
  IMPORT_SOURCES,
  importConnections,
  ImportPick,
  ImportPreviewResult,
  ImportSource,
  initialPicks,
  MAX_IMPORT_BYTES,
} from './importPreview';
import { ManualEntryDrawer } from './ManualEntryDrawer';
import { formatFileSize, readAsBase64, shortName } from './manualEntry';
import { ImportHistoryLine, useImportHistory } from './useImportHistory';

/**
 * Revenue → Imports: a processor export's source and drop zone, its preview
 * (BAR-3028), `+ Manual entry` and the import history (BAR-3031).
 */

const VIEW_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

const DROP_STYLE: CSSProperties = {
  marginTop: 10,
  border: '1.5px dashed rgba(0, 0, 0, 0.18)',
  borderRadius: 10,
  padding: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  cursor: 'pointer',
  background: 'var(--ev-color-warm-white, #FBF9F5)',
};

const DROP_ACTIVE_STYLE: CSSProperties = {
  ...DROP_STYLE,
  borderColor: 'rgba(36, 55, 33, 0.5)',
  background: 'rgba(36, 55, 33, 0.03)',
};

const NEW_LINE_STYLE: CSSProperties = { background: 'rgba(36, 55, 33, 0.05)' };

type LoadedFile = { name: string; data: string };

const BUTTON_STYLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid rgba(0, 0, 0, 0.10)',
  background: '#fff',
  color: 'black',
};

const GHOST_BUTTON_STYLE: CSSProperties = { ...BUTTON_STYLE, padding: '3px 10px', border: '1px solid transparent' };

const MONEY = { align: 'right' as const };

export function ImportsView(): ReactElement {
  const medplum = useMedplum();
  const history = useImportHistory();
  const { practitioners } = useProviderDirectory();
  const [, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [source, setSource] = useState<ImportSource>('CHERRY');
  const [file, setFile] = useState<LoadedFile>();
  const [preview, setPreview] = useState<ImportPreviewResult>();
  const [picks, setPicks] = useState<Record<string, ImportPick>>({});
  const [reading, setReading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [newLineId, setNewLineId] = useState<string>();
  const fileInput = useRef<HTMLInputElement>(null);
  const sourceOption = IMPORT_SOURCES.find((option) => option.value === source) ?? IMPORT_SOURCES[0];

  useEffect(() => {
    if (history.error) alert.error(`Couldn't load the import history: ${history.error}`);
  }, [history.error]);

  const load = async (picked: File): Promise<void> => {
    if (picked.size > MAX_IMPORT_BYTES) {
      alert.error(`${picked.name} is over ${formatFileSize(MAX_IMPORT_BYTES)}`);
      return;
    }
    setReading(true);
    try {
      const loaded = { name: picked.name, data: await readAsBase64(picked) };
      // Previewing writes nothing, so a timed-out preview is tried once more (BAR-3125).
      const result = await fetchBotRead<ImportPreviewResult>(medplum, 'create-treatment-payment', {
        operation: 'previewImport',
        importFile: { source, fileName: loaded.name, data: loaded.data },
      });
      setFile(loaded);
      setPreview(result);
      setPicks(initialPicks(result.matches));
    } catch (error) {
      alert.error(`Couldn't read ${picked.name}: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setReading(false);
    }
  };

  const discard = (): void => {
    setFile(undefined);
    setPreview(undefined);
    setPicks({});
  };

  const runImport = async (): Promise<void> => {
    if (!file || !preview) return;
    setImporting(true);
    try {
      // On a failure the history re-reads: an import that timed out may still have landed (BAR-3125).
      const { imported } = await fetchBotWrite<{ imported: { importBatchId: string; imported: number } }>(
        medplum,
        'create-treatment-payment',
        {
          operation: 'importLedgerFile',
          importFile: { source, fileName: file.name, data: file.data },
          connections: importConnections(preview.preview.rows, picks),
        },
        history.reload
      );
      showUndoToast(`${imported.imported} rows imported to Revenue`);
      setNewLineId(imported.importBatchId);
      discard();
      history.reload();
    } catch (error) {
      alert.error(`Couldn't import ${file.name}: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  const pick = useCallback(
    (reference: string, next: ImportPick | null): void =>
      setPicks((current) => {
        const { [reference]: _dropped, ...rest } = current;
        return next ? { ...rest, [reference]: next } : rest;
      }),
    []
  );

  // The ledger's No patient chip over all time: the one place connections are fixed.
  const goToUnconnected = useCallback((): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', 'ledger');
      next.set(LEDGER_NO_PATIENT_PARAM, '1');
      return next;
    });
  }, [setSearchParams]);

  const suggestions = useMemo(
    () => Object.fromEntries(Object.entries(preview?.matches ?? {}).filter(([, match]) => !match.exact)),
    [preview]
  );

  const columns = useMemo((): MRT_ColumnDef<ImportHistoryLine>[] => {
    const nameOf = (actingUser: string | null): string =>
      shortName(practitioners.find((practitioner) => getReferenceString(practitioner) === actingUser));

    const openProof = (binaryId: string): void => {
      fetchBot<{ url: string }>(medplum, 'finance-proof', { operation: 'link', binaryId })
        .then(({ url }) => window.open(url, '_blank', 'noopener'))
        .catch((error: unknown) =>
          alert.error(`Couldn't open the proof: ${error instanceof Error ? error.message : 'unknown error'}`)
        );
    };

    return [
      {
        id: 'uploaded',
        header: 'Uploaded',
        size: 150,
        enableSorting: false,
        Cell: ({ row }) => (
          <LedgerCell>{row.original?.createdAt ? formatLedgerDate(row.original.createdAt) : ''}</LedgerCell>
        ),
      },
      {
        id: 'by',
        header: 'By',
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => <LedgerCell>{nameOf(row.original?.actingUser ?? null)}</LedgerCell>,
      },
      {
        id: 'source',
        header: 'Source',
        size: 96,
        enableSorting: false,
        Cell: ({ row }) => <LedgerCell>{sourceLabel(row.original?.source ?? null)}</LedgerCell>,
      },
      {
        id: 'file',
        header: 'File',
        size: 200,
        grow: true,
        enableSorting: false,
        Cell: ({ row }) => {
          const line = row.original;
          if (!line?.fileName) return null;
          return line.proofBinaryId ? (
            <UnstyledButton
              title={line.fileName}
              onClick={() => openProof(line.proofBinaryId as string)}
              style={{ fontSize: 13, lineHeight: '18px', textDecoration: 'underline', maxWidth: '100%' }}
            >
              <Text fz={13} truncate>
                {line.fileName}
              </Text>
            </UnstyledButton>
          ) : (
            <LedgerCell title={line.fileName}>{line.fileName}</LedgerCell>
          );
        },
      },
      {
        id: 'imported',
        header: 'Imported',
        size: 80,
        enableSorting: false,
        mantineTableHeadCellProps: MONEY,
        mantineTableBodyCellProps: MONEY,
        Cell: ({ row }) => <LedgerCell style={{ textAlign: 'right' }}>{row.original?.imported}</LedgerCell>,
      },
      {
        id: 'skipped',
        header: 'Skipped',
        size: 80,
        enableSorting: false,
        mantineTableHeadCellProps: MONEY,
        mantineTableBodyCellProps: MONEY,
        Cell: ({ row }) => <LedgerCell style={{ textAlign: 'right' }}>{row.original?.skipped}</LedgerCell>,
      },
      {
        id: 'noPatient',
        header: 'No patient',
        size: 90,
        enableSorting: false,
        mantineTableHeadCellProps: MONEY,
        mantineTableBodyCellProps: MONEY,
        Cell: ({ row }) => {
          const open = row.original?.noPatient ?? 0;
          return (
            <LedgerCell style={{ textAlign: 'right' }} color={open ? ORBIT_NEEDS_ACTION : ORBIT_DISABLED}>
              {open}
            </LedgerCell>
          );
        },
      },
      {
        id: 'connect',
        header: '',
        size: 90,
        enableSorting: false,
        mantineTableBodyCellProps: MONEY,
        Cell: ({ row }) =>
          row.original?.noPatient ? (
            <UnstyledButton style={GHOST_BUTTON_STYLE} onClick={goToUnconnected}>
              Connect
            </UnstyledButton>
          ) : null,
      },
    ];
  }, [medplum, practitioners, goToUnconnected]);

  return (
    <Box style={VIEW_STYLE}>
      {preview && file ? (
        <ImportPreviewTable
          fileName={file.name}
          rows={preview.preview.rows}
          picks={picks}
          suggestions={suggestions}
          importing={importing}
          onPick={pick}
          onCancel={discard}
          onImport={() => void runImport()}
        />
      ) : (
        <Box px={16} pb={16}>
          <Group gap={10}>
            <SegmentedControl
              size="xs"
              data={IMPORT_SOURCES.map(({ value, label }) => ({ value, label }))}
              value={source}
              onChange={(value) => setSource(value as ImportSource)}
            />
            <UnstyledButton style={{ ...BUTTON_STYLE, marginLeft: 'auto' }} onClick={() => setDrawerOpen(true)}>
              + Manual entry
            </UnstyledButton>
          </Group>
          <UnstyledButton
            style={{ ...(dragging ? DROP_ACTIVE_STYLE : DROP_STYLE), width: '100%' }}
            onClick={() => fileInput.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const dropped = event.dataTransfer.files[0];
              if (dropped) void load(dropped);
            }}
            disabled={reading}
            data-testid="import-drop"
          >
            {reading ? <Loader size="sm" /> : <IconUpload size={22} stroke={1.6} color={ORBIT_GRAY} />}
            <Box>
              <Text fz={13} fw={600}>
                {sourceOption.dropTitle}
              </Text>
              <Text fz={12} c={ORBIT_GRAY} mt={2}>
                {sourceOption.accept}
              </Text>
            </Box>
          </UnstyledButton>
          <input
            ref={fileInput}
            type="file"
            accept={sourceOption.accept}
            hidden
            data-testid="import-file-input"
            onChange={(event) => {
              const picked = event.currentTarget.files?.[0];
              event.currentTarget.value = '';
              if (picked) void load(picked);
            }}
          />
        </Box>
      )}
      <Text fz={12} fw={600} c={ORBIT_GRAY} px={16} pt={14} pb={6}>
        Import history
      </Text>
      <HubTable
        columns={columns}
        data={history.imports}
        loading={history.loading}
        emptyText="No imports yet"
        emptyFallback={
          history.error && <LoadFailed what="the import history" error={history.error} onRetry={history.reload} />
        }
        getRowProps={(line) => ({ style: line?.id && line.id === newLineId ? NEW_LINE_STYLE : undefined })}
      />
      <ManualEntryDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} onAdded={history.reload} />
    </Box>
  );
}
